import { downloadInternalRequestFile } from "@synthesis/shared-services";
import { useGet4, useSetFlag } from "@synthesis/shared-services/internal/payment/hooks";
import type { PaymentView, PaymentViewPaymentFlagEnumKey } from "@synthesis/shared-services/internal/payment/types";
import { paymentViewPaymentFlagEnum } from "@synthesis/shared-services/internal/payment/types";
import { IconCloseCircleFill, IconPayment } from "@synthesis/shared-icons";
import { Button, RadioGroup, setPromiseSnack, setSnack } from "@synthesis/shared-ui";
import { cn, useDownload } from "@synthesis/shared-utils";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import dayjs from "dayjs";

import { get4QueryKey } from "@synthesis/shared-services/internal/payment/hooks";
import { usePageStore } from "../../application-detail-page.lib";
import { DetailTabPlaceholder } from "../detail-tab-placeholder";
import { PaymentExternalContent } from "./payment-external-content";
import { PaymentProofFileChip } from "./payment-proof-file-chip";
import { useUserRoles } from "../../../../shared/hooks/use-user-roles";

const PAYMENT_FLAG_OPTIONS: ReadonlyArray<{ value: PaymentViewPaymentFlagEnumKey; label: string }> = [
  { value: paymentViewPaymentFlagEnum.UNDER_REVIEW, label: "На рассмотрении" },
  { value: paymentViewPaymentFlagEnum.NO_PAYMENT, label: "Без оплаты" },
  { value: paymentViewPaymentFlagEnum.PAID, label: "Платная услуга" },
] as const;

type PaymentContentProps = {
  requestId: string;
  clientType: "external" | "internal";
  className?: string;
};

type PaymentFlagFormValues = {
  paymentFlag: PaymentViewPaymentFlagEnumKey;
};

type PaymentFlagRadioGroupProps = {
  value: PaymentViewPaymentFlagEnumKey;
  disabled: boolean;
  requestId: string;
  className?: string;
};

const PaymentFlagRadioGroup = ({ value, disabled, requestId, className }: PaymentFlagRadioGroupProps) => {
  const queryClient = useQueryClient();
  const setFlagMutation = useSetFlag();

  const methods = useForm<PaymentFlagFormValues>({
    defaultValues: { paymentFlag: value },
    values: { paymentFlag: value },
  });

  const handleFlagChange = (rawValue: string) => {
    const newFlag = rawValue as PaymentViewPaymentFlagEnumKey;

    if (newFlag === value) {
      return;
    }

    const promise = setFlagMutation.mutateAsync(
      { path: { id: requestId }, body: { flag: newFlag } },
      {
        onSuccess: (updatedView) => {
          queryClient.setQueryData(get4QueryKey({ path: { id: requestId } }), updatedView);
        },
      },
    );

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: {
          pending: "Выполняется...",
          fulfilled: "Признак оплаты обновлён",
          rejected: "Не удалось обновить признак оплаты. Попробуйте еще раз",
        },
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <div className={cn("flex flex-col gap-2", className)}>
        <p className="paragraph-base text-type-minor">Условия оплаты услуг РЖД</p>
        <RadioGroup
          name="paymentFlag"
          horizontal
          gap="md"
          disabled={disabled || setFlagMutation.isPending}
          onClick={handleFlagChange}
          renderRadioGroupElements={PAYMENT_FLAG_OPTIONS.map((option) => ({
            id: `payment-flag-${option.value}`,
            value: option.value,
            label: option.label,
          }))}
        />
      </div>
    </FormProvider>
  );
};

const InternalPaymentContent = ({ paymentView, requestId, className }: { paymentView: PaymentView; requestId: string; className?: string }) => {
  const userRoles = useUserRoles();
  const pageStore = usePageStore();
  const downloadFile = useDownload();
  const canEdit = paymentView.canEditFlag === true && userRoles?.isInternalApparatusNg;
  const flag = paymentView.paymentFlag ?? paymentViewPaymentFlagEnum.UNDER_REVIEW;
  const isPaid = flag === paymentViewPaymentFlagEnum.PAID;
  const proofFileId = paymentView.proofFileId ?? null;
  const proofFileName = paymentView.proofFileName ?? null;

  const handleDownloadProof = async (fileId: string, fileName: string) => {
    try {
      const blob = await downloadInternalRequestFile(fileId);
      downloadFile({ id: fileId, baseName: fileName }, blob);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setSnack({
        title: "Не удалось скачать файл",
        description: message,
        icon: <IconCloseCircleFill className="size-4" />,
      });
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <PaymentFlagRadioGroup value={flag} disabled={!canEdit || Boolean(paymentView.invoiceAmount)} requestId={requestId} />

      {isPaid && (
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <span className="subtitle-sm text-type-light w-56">Стоимость по счёту, ₽</span>
            <span className="paragraph-base text-type-common">
              {paymentView.invoiceAmount ? paymentView.invoiceAmount.toLocaleString("ru-RU") : "—"}
            </span>
          </div>
          <div className="flex gap-2">
            <span className="subtitle-sm text-type-light w-56">Номер счёта</span>
            <span className="paragraph-base text-type-common">{paymentView.invoiceNumber ?? "—"}</span>
          </div>
          <div className="flex gap-2">
            <span className="subtitle-sm text-type-light w-56">Дата выставления счёта</span>
            <span className="paragraph-base text-type-common">
              {paymentView.invoiceSentAt ? dayjs(paymentView.invoiceSentAt).format("DD.MM.YYYY HH:mm") : "—"}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <span className="subtitle-sm text-type-light w-56 shrink-0">Подтверждение оплаты</span>
            {proofFileName ? (
              <PaymentProofFileChip
                fileName={proofFileName}
                onDownload={proofFileId ? () => handleDownloadProof(proofFileId, proofFileName) : undefined}
              />
            ) : (
              <span className="paragraph-base text-type-common">—</span>
            )}
          </div>
        </div>
      )}

      {isPaid && !paymentView.invoiceAmount && (
        <Button
          size="sm"
          theme="secondary"
          className="self-start"
          leftIcon={<IconPayment className="size-5" />}
          onClick={() => pageStore?.setPopupAction({ type: "issue-invoice", requestId })}
        >
          Выставить счет
        </Button>
      )}
    </div>
  );
};

export const PaymentContent = ({ requestId, clientType, className }: PaymentContentProps) => {
  const { data: paymentView, isLoading } = useGet4(
    { path: { id: requestId } },
    {
      query: { enabled: clientType === "internal" && !!requestId },
    },
  );

  if (clientType === "external") {
    return <PaymentExternalContent requestId={requestId} className={className} />;
  }

  if (isLoading) {
    return <DetailTabPlaceholder className={className} title="Загрузка данных об оплате" description="Пожалуйста, подождите..." />;
  }

  if (!paymentView) {
    return <DetailTabPlaceholder className={className} title="Данные об оплате недоступны" description="Не удалось загрузить информацию об оплате" />;
  }

  return <InternalPaymentContent paymentView={paymentView} requestId={requestId} className={className} />;
};
